import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Network,
  RefreshCw,
  Rows3,
} from 'lucide-react';

import {
  api,
  type CarrierInitializationDiscoveryState,
  type CarrierInitializationMappingDrafts,
  type CarrierInitializationServiceDefinitionsState,
} from '@/services/api';
import { CarrierDiscoveryProgress } from './CarrierDiscoveryProgress';
import { getServiceSetupPresentation } from './carrierInitializationOverviewState';

interface CarrierInitializationOverviewProps {
  disabled?: boolean;
  onInvokeCommand?: (command: string) => boolean | Promise<boolean>;
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Carrier setup could not be loaded.';
}

export function CarrierInitializationOverview({
  disabled = false,
  onInvokeCommand,
}: CarrierInitializationOverviewProps) {
  const [services, setServices] = useState<CarrierInitializationServiceDefinitionsState | null>(null);
  const [discovery, setDiscovery] = useState<CarrierInitializationDiscoveryState | null>(null);
  const [mapping, setMapping] = useState<CarrierInitializationMappingDrafts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextServices, nextDiscovery, nextMapping] = await Promise.all([
        api.carrierInitialization.getServiceDefinitions(),
        api.carrierInitialization.getDiscovery(),
        api.carrierInitialization.getMappingDrafts(),
      ]);
      setServices(nextServices);
      setDiscovery(nextDiscovery);
      setMapping(nextMapping);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const invoke = (command: string) => {
    if (disabled || !onInvokeCommand) return;
    void onInvokeCommand(command);
  };

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-surface-500 dark:text-surface-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading Carrier setup…
      </div>
    );
  }

  const serviceCount = services?.definitions.length ?? 0;
  const servicePresentation = getServiceSetupPresentation(serviceCount);
  const carrierCount = mapping?.drafts.length ?? 0;
  const carrierDescription = !discovery
    ? 'Carrier discovery has not started.'
    : !discovery.progress.complete
      ? `${discovery.progress.counts.accounts} Inbound trunks and ${discovery.progress.counts.vendors} Outbound trunks found so far.`
      : `${carrierCount} Carriers organized · ${(mapping?.unassignedAccounts.length ?? 0) + (mapping?.unassignedVendors.length ?? 0)} unassigned · ${mapping?.ignoredRecords.length ?? 0} ignored`;

  return (
    <section aria-label="Carrier setup overview" className="mt-3 w-[min(44rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-surface-200 bg-surface-50 shadow-sm dark:border-surface-700 dark:bg-surface-950">
      <header className="border-b border-surface-200 bg-white px-4 py-3.5 dark:border-surface-800 dark:bg-surface-900">
        <p className="text-[0.65rem] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Carrier setup</p>
        <h3 className="mt-1 text-base font-semibold text-surface-950 dark:text-white">What would you like to work on?</h3>
        <p className="mt-1 text-xs leading-5 text-surface-500 dark:text-surface-400">Services and Carriers can be updated independently whenever your tenant changes.</p>
      </header>

      {error && (
        <div role="alert" className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
          <button type="button" onClick={() => void load()} className="ml-auto flex shrink-0 items-center gap-1 font-semibold"><RefreshCw className="h-3 w-3" />Retry</button>
        </div>
      )}

      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <article className={`rounded-lg border p-3.5 ${servicePresentation.missing ? 'border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20' : 'border-surface-200 bg-white dark:border-surface-800 dark:bg-surface-900'}`}>
          <div className="flex items-start gap-3">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${servicePresentation.missing ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'}`}>
              {servicePresentation.missing ? <Rows3 className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-surface-950 dark:text-white">Services</h4>
              <p className="mt-1 text-xs leading-5 text-surface-600 dark:text-surface-300">
                {servicePresentation.description}
              </p>
              <button type="button" disabled={disabled || !onInvokeCommand} onClick={() => invoke('/manage-services')} className="mt-3 h-8 rounded-lg bg-surface-900 px-3 text-xs font-semibold text-white hover:bg-surface-700 disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200">
                {servicePresentation.actionLabel}
              </button>
            </div>
          </div>
        </article>

        <article className="rounded-lg border border-surface-200 bg-white p-3.5 dark:border-surface-800 dark:bg-surface-900">
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-950 dark:text-primary-300">
              <Network className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-surface-950 dark:text-white">Carriers</h4>
              <p className="mt-1 text-xs leading-5 text-surface-600 dark:text-surface-300">{carrierDescription}</p>
              <button type="button" disabled={disabled || (discovery?.progress.complete ? !onInvokeCommand : discoveryOpen)} onClick={() => {
                // Discovery runs here; Services are optional (ADR-0018). Reopening
                // /organize-carriers before discovery completes would show this overview again.
                if (discovery?.progress.complete) invoke('/organize-carriers');
                else setDiscoveryOpen(true);
              }} className="mt-3 h-8 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-800 hover:border-amber-500 hover:text-amber-700 disabled:opacity-50 dark:border-surface-700 dark:text-surface-100 dark:hover:text-amber-300">
                {discovery?.progress.complete ? 'Organize Carriers' : discovery ? 'Continue Carrier setup' : 'Start Carrier setup'}
              </button>
            </div>
          </div>
        </article>
      </div>

      {discoveryOpen && !discovery?.progress.complete && (
        <div className="border-t border-surface-200 px-4 pb-4 dark:border-surface-800">
          <CarrierDiscoveryProgress disabled={disabled} autoStart />
        </div>
      )}
    </section>
  );
}
